import { ExercisePrefixes, Exercises, ExerciseTypes } from "../enums/exercises";
import { uniqueId, shuffle } from "lodash";
import {
    TestDetailsWord,
    UnitCard,
    UserOwnCard,
    VerbtrainingSelectedWord,
    TestQuestionModel,
    SidebarSelectedWordsList,
    SidebarSelectedWord,
    Exercise,
    ExerciseCreationModel,
} from "p6m-p6u";
import striptags from "striptags";
import { ExerciseDirections } from "../enums/directions";
import { Locales } from "../hooks/useExerciseDescriptionStructure";

export const generateExerciseId = (
    exType: Exercises,
    testContent: Array<TestQuestionModel>,
    suggestionExerciseIds?: Array<string>
): string => {
    const prefix = ExercisePrefixes.get(exType) as string;
    const testContentExerciseIds = testContent.map((e) => e.exerciseId);
    const exerciseIds = suggestionExerciseIds
        ? [...testContentExerciseIds, ...suggestionExerciseIds]
        : testContentExerciseIds;

    const _isUnique = (generatedId: string) => exerciseIds.findIndex((id) => id === generatedId) === -1;
    const _generateId = () => uniqueId(ExercisePrefixes.get(exType));
    const _generateConsecutiveId = () => {
        const exerciseIdsWithSamePrefix = exerciseIds.filter((id) => id && id.includes(prefix)) as string[];
        const idNumbers = exerciseIdsWithSamePrefix.map((id) => id.replace(prefix, ""));
        const sortedIdNumbers = idNumbers.sort((a, b) => +a - +b);
        const consecutiveNumber = +sortedIdNumbers[sortedIdNumbers.length - 1] + 1;
        return prefix + consecutiveNumber;
    };

    const hopefullyUniqueId = _generateId();
    return _isUnique(hopefullyUniqueId) ? hopefullyUniqueId : _generateConsecutiveId();
};

const createExercise = (exerciseType: Exercises, words: SidebarSelectedWord[]): Exercise => {
    return {
        type: exerciseType,
        words: [...words],
    };
};

export const translateWordFromModelToQuestion = (word: UnitCard | UserOwnCard): TestDetailsWord => {
    if ("answer_text" in word) {
        return {
            answer: word.answer_text || "",
            id: word.id || "",
            question: word.question_text || "",
            unit_id: String(word.unitId) || "",
            unit_name: "",
            gap_sentence: word.gap_sentence || generateGapSentence(word) || "",
            answer_example: word.answer_example || "",
        };
    } else if ("isUserCard" in word) {
        return {
            answer: word.answer || "",
            id: word.id || "",
            question: word.question || "",
            unit_id: String(word.unitId) || "",
            unit_name: "",
            gap_sentence: "",
            answer_example: "",
            isUserCard: true,
            ownerId: word.ownerId,
        };
    } else {
        return {};
    }
};
export const translateWordFromQuestionToModel = (word: TestDetailsWord): UnitCard | UserOwnCard => {
    if (word.isUserCard) {
        return {
            answer: word.answer,
            id: word.id,
            ownerId: word.ownerId,
            question: word.question,
            unitId: word.unit_id,
            isUserCard: true,
        };
    }
    return {
        hidden: false,
        answer_text: word.answer || "",
        question_text: word.question || "",
        answer_example: word.answer_example || "",
        gap_sentence: word.gap_sentence || "",
        id: word.id || "",
        unitId: String(word.unit_id) || "",
    };
};

export const sanitizeText = (word: string): string => {
    return striptags(word.replace(/&(.*?);/g, ""));
};

export const generateGapSentence = (wordData: UnitCard): string => {
    // The word object here, is the transformed one in the words selection.
    let sentenceToReturn = "";

    if (wordData.answer_example && wordData.answer_text) {
        let answerText = sanitizeText(wordData.answer_text);
        let answerExample = sanitizeText(wordData.answer_example);

        var wordAnswerArray = answerExample.split(" ");
        if (wordAnswerArray.indexOf(answerText) !== -1) {
            var indexOfWord = wordAnswerArray.indexOf(answerText);
            wordAnswerArray[indexOfWord] = "_".repeat(Math.ceil(answerText.length * 2));
            sentenceToReturn = wordAnswerArray.join(" ");
        } else {
            var answerToReplace = answerText;
            sentenceToReturn = answerExample.replace(
                answerToReplace,
                "_".repeat(Math.ceil(answerToReplace.length * 2))
            );
        }
    }

    // If the sentence doesnt actually have a gap, then dont return it.
    return sentenceToReturn.indexOf("__") === -1 ? "" : sentenceToReturn;
};

export const getExerciseKeyFromExerciseName = (questionMode: string): Exercises | null => {
    if (questionMode) {
        let exerciseTypesVal = Array.from(ExerciseTypes).find(([key, val]) => val === questionMode);
        if (exerciseTypesVal) {
            return exerciseTypesVal[0] as Exercises;
        }
    }
    return null;
};

export const updateWordsInProgressDirection = (
    words: Array<TestDetailsWord | VerbtrainingSelectedWord>,
    direction: ExerciseDirections
): Array<TestDetailsWord | VerbtrainingSelectedWord> => {
    const exerciseWords = [...words];
    for (const word of exerciseWords) {
        updateSingleWordInProgressDirection(word, direction);
    }
    return exerciseWords;
};

export const updateSingleWordInProgressDirection = (
    word: TestDetailsWord | VerbtrainingSelectedWord,
    direction: ExerciseDirections
) => {
    if ("id" in word) {
        const weight =
            direction === ExerciseDirections.QUESTION_TO_ANSWER
                ? 0
                : direction === ExerciseDirections.ANSWER_TO_QUESTION
                ? 0.9
                : Math.random();
        word.isQuestionShown = weight <= 0.5;
    }
    return word;
};

const _countQuestionsShown = (words: Array<TestDetailsWord | VerbtrainingSelectedWord>) => {
    return words.reduce((acc, currentWord) => {
        if ("id" in currentWord && currentWord?.isQuestionShown) {
            return acc + 1;
        } else {
            return acc;
        }
    }, 0);
};

export const getExerciseDirection = (exercise: ExerciseCreationModel) => {
    // update overall direction
    const amountQuestionShown = _countQuestionsShown(exercise.wordsInProgress ?? []);
    const amountWords = exercise.wordsInProgress ? exercise.wordsInProgress.length : 0;

    const direction =
        amountQuestionShown === amountWords // all showing their question
            ? ExerciseDirections.QUESTION_TO_ANSWER
            : amountQuestionShown === 0 // none showing their question
            ? ExerciseDirections.ANSWER_TO_QUESTION
            : ExerciseDirections.RANDOM;

    return direction;
};

export const createTestSuggestionData = (
    testContent: TestQuestionModel[],
    testToUse: TestQuestionModel[] | undefined,
    availableVocabulary: SidebarSelectedWord[],
    vocabularyDrawerContent: SidebarSelectedWordsList,
    getDefaultDescription: (exerciseType: Exercises, locale: Locales) => string
) => {
    const testSuggestionData = testToUse ? [...testToUse] : [...testContent];

    const exercises = buildSuggestedExercises(availableVocabulary);
    const { exercisesData, vocabularyDrawerContent: newVocabularyDrawerContent } = createNewSuggestionExercisesData(
        exercises,
        vocabularyDrawerContent,
        getDefaultDescription,
        testContent
    );

    testSuggestionData.push(...exercisesData);

    return { testSuggestionData, newVocabularyDrawerContent };
};

const MIN_WORDS_PER_EXERCISE = 3;
const MAX_CONNECT_WORDS = 5;
const GAP_SHARE = 0.6;

interface ExerciseCounts {
    connectCount: number;
    gapCount: number;
    tableCount: number;
}

/**
 * Decides how many words each exercise gets — pure arithmetic, no word objects.
 *
 * `maxGapCount` is the number of gap-eligible words available (0 when there aren't
 * enough for a gap exercise), which caps how many words FILL_GAP can take.
 */
const computeExerciseCounts = (total: number, maxGapCount: number): ExerciseCounts => {
    // If no gap-eligible words → spread across STANDARD + CONNECT instead of one big STANDARD
    if (maxGapCount === 0) {
        const connectCount = Math.min(MAX_CONNECT_WORDS, total - MIN_WORDS_PER_EXERCISE);
        return { connectCount, gapCount: 0, tableCount: total - connectCount };
    }

    // First attempt: split into three exercises (STANDARD, CONNECT_WORDS, FILL_GAP)
    let connectCount = Math.min(MAX_CONNECT_WORDS, total - 2 * MIN_WORDS_PER_EXERCISE);
    let gapCount = Math.min(Math.round((total - connectCount) * GAP_SHARE), maxGapCount);
    let tableCount = total - connectCount - gapCount;

    // Drop connect (split into two) if any of the three exercises would fall below the minimum
    if (
        connectCount < MIN_WORDS_PER_EXERCISE ||
        gapCount < MIN_WORDS_PER_EXERCISE ||
        tableCount < MIN_WORDS_PER_EXERCISE
    ) {
        connectCount = 0;
        gapCount = Math.min(Math.round(total * GAP_SHARE), maxGapCount);
        tableCount = total - gapCount;
    }

    // Shift words from gap to table so the table exercise reaches the minimum
    if (tableCount < MIN_WORDS_PER_EXERCISE && gapCount > MIN_WORDS_PER_EXERCISE) {
        const shift = MIN_WORDS_PER_EXERCISE - tableCount;
        gapCount -= shift;
        tableCount += shift;
    }

    return { connectCount, gapCount, tableCount };
};

/**
 * Distributes words across up to 3 exercise types. Each exercise is only created when it
 * reaches ≥ MIN_WORDS_PER_EXERCISE (3) words. Total < 6 produces a single exercise.
 *
 * Distribution: FILL_GAP ≈ 60%, STANDARD ≈ 40% of non-connect words.
 * CONNECT_WORDS starts at min(5, total − 6) and is dropped to 0 when any exercise would fall below 3 words.
 * Gap-eligible words may be shifted to STANDARD when needed to keep STANDARD ≥ 3.
 * When there aren't enough gap-eligible words for a FILL_GAP, the words spread across STANDARD + CONNECT_WORDS.
 *
 * Order: STANDARD → CONNECT_WORDS → FILL_GAP
 */
const buildSuggestedExercises = (vocabulary: SidebarSelectedWord[]) => {
    const words = shuffle(vocabulary);
    const total = words.length;

    const isGapEligible = (w: SidebarSelectedWord) =>
        w.wordContent && "gap_sentence" in w.wordContent && w.wordContent.gap_sentence !== "";

    const gapEligibleWords = words.filter(isGapEligible);
    const nonGapWords = words.filter((w) => !isGapEligible(w));
    const canFillGapExercise = gapEligibleWords.length >= MIN_WORDS_PER_EXERCISE;

    // Fewer than two exercises' worth of words → 1 exercise. Use FILL_GAP when every
    // word is gap-eligible; otherwise STANDARD so the non-gap words aren't dropped.
    if (total < MIN_WORDS_PER_EXERCISE * 2) {
        if (canFillGapExercise && gapEligibleWords.length === total) {
            return [createExercise(Exercises.FILL_GAP, gapEligibleWords)];
        }
        return [createExercise(Exercises.STANDARD, words)];
    }

    // Cap gap words at what's actually eligible (0 when there aren't enough for a gap exercise)
    const maxGapCount = canFillGapExercise ? gapEligibleWords.length : 0;
    const { connectCount, gapCount } = computeExerciseCounts(total, maxGapCount);

    // Gap-eligible words fill FILL_GAP first; any beyond gapCount join the non-gap words for the other exercises
    const gapWords = gapEligibleWords.slice(0, gapCount);
    const tableConnectPool = [...nonGapWords, ...gapEligibleWords.slice(gapCount)];
    const connectWords = tableConnectPool.slice(0, connectCount);
    const standardWords = tableConnectPool.slice(connectCount);

    const exercises: Exercise[] = [];
    if (standardWords.length >= MIN_WORDS_PER_EXERCISE)
        exercises.push(createExercise(Exercises.STANDARD, standardWords));
    if (connectWords.length >= MIN_WORDS_PER_EXERCISE)
        exercises.push(createExercise(Exercises.CONNECT_WORDS, connectWords));
    if (gapWords.length >= MIN_WORDS_PER_EXERCISE) exercises.push(createExercise(Exercises.FILL_GAP, gapWords));
    return exercises;
};

const createNewSuggestionExercisesData = (
    exercises: Exercise[],
    vocabularyDrawerContent: SidebarSelectedWordsList,
    getDefaultDescription: (exerciseType: Exercises, locale: Locales) => string,
    testContent: TestQuestionModel[]
) => {
    const exercisesData: TestQuestionModel[] = [];
    const exerciseIds: string[] = [];
    exercises.forEach((exercise) => {
        const exerciseId = generateExerciseId(exercise.type, testContent, exerciseIds);
        exerciseIds.push(exerciseId);
        const exerciseWords: TestDetailsWord[] = [];
        exercise.words.forEach((w) => {
            if (w.wordContent?.id) {
                const exerciseIds: string[] = vocabularyDrawerContent[w.wordContent.id].exerciseIds || [];
                exerciseIds.push(exerciseId);
                vocabularyDrawerContent[w.wordContent.id].exerciseIds = exerciseIds;
                vocabularyDrawerContent[w.wordContent.id].isInMoreThanOneExercise = exerciseIds.length > 1;

                exerciseWords.push(translateWordFromModelToQuestion(w.wordContent));
            }
        });

        // mix direction for table exercise
        if (exercise.type === Exercises.STANDARD) {
            updateWordsInProgressDirection(exerciseWords, ExerciseDirections.RANDOM);
        }

        const exerciseData: TestQuestionModel = {
            questionMode: ExerciseTypes.get(exercise.type),
            extraComments: getDefaultDescription(exercise.type, Locales.DE),
            selectedWords: [...exerciseWords],
            automaticallyCreated: true,
            exerciseId,
        };
        if (exercise.type === Exercises.STANDARD) {
            exerciseData.direction = 2;
        }
        exercisesData.push(exerciseData);
    });

    return { exercisesData, vocabularyDrawerContent };
};

export const createNewExercise = (
    exerciseModel: ExerciseCreationModel | TestQuestionModel
): ExerciseCreationModel | TestQuestionModel => {
    exerciseModel.automaticallyCreated = false;
    exerciseModel.selectedWords = exerciseModel.wordsInProgress || [];

    return exerciseModel;
};

/** add new exercise to or replace edited exercise of testContent  */
export const createUpdatedTestContent = (
    testContent: TestQuestionModel[],
    exerciseModel: ExerciseCreationModel,
    newExercise: ExerciseCreationModel
): TestQuestionModel[] => {
    const { status, wordsInProgress, ...testQuestionExercise } = newExercise;

    if (exerciseModel.status === "NEW") {
        return [...testContent, testQuestionExercise];
    } else {
        // replace edited Exercise with newExercises
        const currentTestContent = [...testContent];
        const exerciseToReplaceIndex = testContent.findIndex((t) => t.exerciseId === exerciseModel.exerciseId);

        if (exerciseToReplaceIndex > -1) {
            currentTestContent.splice(exerciseToReplaceIndex, 1, testQuestionExercise);
        } else {
            currentTestContent.push(testQuestionExercise);
        }
        return currentTestContent;
    }
};

/**
 * create updated VocabularDrawerContent
 * with words containg
 * - the new _exerciseIds_,
 * - the updated property _isInMoreThanOneExercise_,
 */
export const createUpdatedDrawerContent = (
    drawerContent: SidebarSelectedWordsList,
    newExercise: ExerciseCreationModel
): SidebarSelectedWordsList => {
    // Update word ids in drawer model.
    for (const drawerWord of Object.values(drawerContent)) {
        if (!drawerWord.wordContent || !drawerWord.wordContent.id) {
            continue;
        }
        // Remove old exerciseId from the VocabularyDrawerContent
        if (drawerWord.exerciseIds && drawerWord.exerciseIds.includes(newExercise.exerciseId!)) {
            drawerWord.exerciseIds = drawerWord.exerciseIds.filter((e) => e !== newExercise.exerciseId);
        }
        // add newExercise's id to VocabularyDrawerContent
        const wordInExercise = newExercise.selectedWords?.find((w) => {
            const wordId = "id" in w ? w.id : "ID" in w ? w.ID : "";
            return drawerWord.wordContent?.id === wordId;
        });
        if (wordInExercise && newExercise.exerciseId) {
            if (!drawerWord.exerciseIds || !drawerWord.exerciseIds.includes(newExercise.exerciseId)) {
                drawerWord.exerciseIds = [...(drawerWord.exerciseIds || []), newExercise.exerciseId];
            }
        }

        // update isInMoreThanOneExercise
        drawerWord.isInMoreThanOneExercise = drawerWord.exerciseIds && drawerWord.exerciseIds.length > 1;

        // update drawerContent
        drawerContent[drawerWord.wordContent.id] = drawerWord;
    }

    return drawerContent;
};
